import { Alert, Button } from 'antd';
import i18n from 'i18next';
import { groupBy, isNil, omit, uniqBy } from 'lodash';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { None } from 'components/data/helpers';
import { assetWithoutFilters, date, site } from 'components/data/inspections';
import { useUploadContext } from 'components/FileUploader';
import { Spinner } from 'components/ui';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAsset,
  type AtlasGqlInspection,
  type AtlasGqlMetadataFieldMapping,
  useGetInspectionsForImageDetailsUploadQuery,
} from 'types/atlas-graphql';
import { STANDARD_COLUMN_SCROLL_Y, TABLE_SCROLL_WIDTH } from 'utils/constants';

import { Steps, UploadTypes } from '../UploadInspectionDataModal';
import { ButtonContainer, ValidationTable } from '../UploadInspectionDataModal.style';

type PreviewProps = {
  uploadType: UploadTypes;
  inspectionIds: string[];
  fieldMappings?: AtlasGqlMetadataFieldMapping[];
  previewData?: {
    [key: string]: string | number;
  }[];
  setStep: Dispatch<SetStateAction<Steps>>;
  handleComplete: (allImagesHaveUrls: boolean) => void;
};

type PreviewRow = {
  id: string | null | undefined;
  inspectionDate: string | null | undefined;
  asset: AtlasGqlAsset | undefined;
  imageCount: number;
  [key: string]: string | number | AtlasGqlAsset | null | undefined;
};

const formatPreviewRow = (
  rows: {
    [key: string]: string | number;
  }[],
  uploadType: UploadTypes,
  inspections: AtlasGqlInspection[],
  taskIdFieldName: string,
  fileUploadIdFieldName: string
): PreviewRow => {
  const [firstRow] = rows;
  const inspection =
    uploadType === UploadTypes.Single && inspections.length === 1
      ? inspections[0]
      : inspections.find(
          ({ taskId, fileUploadId }) =>
            taskId === firstRow[taskIdFieldName] ||
            fileUploadId?.split('_')[1] === firstRow[fileUploadIdFieldName]
        );
  return {
    id: inspection?.id,
    inspectionDate: inspection?.inspectionDate,
    asset: inspection?.asset,
    imageCount: rows.length,
    ...omit(firstRow, [fileUploadIdFieldName, taskIdFieldName]),
  };
};

const dateColumn = { ...omit(date, ['filterDropdown', 'sorter']), width: 120 };
const siteColumn = omit(site, ['filterDropdown', 'sorter']);
const imageCountColumn = {
  key: 'imageCount',
  title: i18n.t('table.imageCount', 'Image Count'),
  dataIndex: 'imageCount',
  width: 80,
  filterProperty: 'imageCount',
};

export const Preview: React.FC<PreviewProps> = ({
  uploadType,
  inspectionIds,
  fieldMappings,
  previewData,
  setStep,
  handleComplete,
}) => {
  const { t } = useTranslation();
  const { uploading } = useUploadContext();

  const { data, loading } = useGetInspectionsForImageDetailsUploadQuery({
    variables: { filterBy: { key: 'id', values: inspectionIds } },
  });

  const {
    rows,
    columns,
    affectedInspectionCount,
    inspectionsNotFound,
    allImagesHaveUrls,
  }: {
    rows: PreviewRow[];
    columns: TTableColumnDef[];
    affectedInspectionCount: number;
    inspectionsNotFound: boolean;
    allImagesHaveUrls: boolean;
  } = useMemo(() => {
    const fileUploadIdFieldName =
      fieldMappings?.find(({ targetFieldName }) => targetFieldName === 'id')?.sourceFieldName ??
      'id';
    const taskIdFieldName =
      fieldMappings?.find(({ targetFieldName }) => targetFieldName === 'taskId')?.sourceFieldName ??
      'taskId';
    const urlFieldName = fieldMappings?.find(
      ({ targetFieldName }) => targetFieldName === 'imageUrl'
    )?.sourceFieldName;

    const allImagesHaveUrls = urlFieldName
      ? !!previewData?.every((dataItem) => !!dataItem[urlFieldName])
      : false;

    let groupedByInspection: { [key: string]: any[] };
    if (uploadType === UploadTypes.Single && inspectionIds.length === 1) {
      // for single inspections, group all images under the inspection id
      groupedByInspection = { [inspectionIds[0]]: previewData ?? [] };
    } else if (previewData?.every((row) => row[taskIdFieldName])) {
      // group by task id
      groupedByInspection = groupBy(previewData, taskIdFieldName);
    } else if (previewData?.every((row) => row[fileUploadIdFieldName])) {
      // group by file upload id
      groupedByInspection = groupBy(previewData, fileUploadIdFieldName);
    } else {
      return {
        rows: [],
        columns: [],
        affectedInspectionCount: 0,
        inspectionsNotFound: true,
        allImagesHaveUrls,
      };
    }

    const inspections = (data?.inspections?.items ?? []) as AtlasGqlInspection[];
    const rows: PreviewRow[] = Object.values(groupedByInspection).map((rows) =>
      formatPreviewRow(rows, uploadType, inspections, taskIdFieldName, fileUploadIdFieldName)
    );

    const columns: TTableColumnDef[] = rows[0]
      ? [
          dateColumn,
          siteColumn,
          assetWithoutFilters,
          imageCountColumn,
          ...(fieldMappings ?? [])
            .filter(
              ({ sourceFieldName }) =>
                ![fileUploadIdFieldName, taskIdFieldName, urlFieldName].includes(sourceFieldName)
            )
            .map(({ sourceFieldName }) => ({
              key: sourceFieldName,
              title: t('inspection.firstImageField', 'First Image {{fieldName}}', {
                fieldName: sourceFieldName,
              }),
              dataIndex: sourceFieldName,
              render: (value: string | number | null | undefined) => {
                // Need isNil, not isEmpty, to handle numeric values correctly
                return isNil(value) ? <None /> : value;
              },
            })),
          ...(urlFieldName
            ? [
                {
                  key: urlFieldName,
                  title: t('inspection.firstImageUrl', 'First Image URL'),
                  dataIndex: urlFieldName,
                  render: (value: string | null | undefined) => (
                    <a
                      title={value ? String(value) : t('components.none', 'None')}
                      href={value ?? undefined}
                    >
                      {value}
                    </a>
                  ),
                },
              ]
            : []),
        ]
      : [];

    return {
      rows,
      allImagesHaveUrls,
      columns,
      affectedInspectionCount: uniqBy(rows, 'id').length,
      inspectionsNotFound: rows.some(({ asset }) => !asset),
    };
  }, [inspectionIds, fieldMappings, previewData, data?.inspections?.items, t]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      {previewData?.length && (
        <>
          <p>
            {t('inspection.uploadDataToInspectionCount', {
              count: affectedInspectionCount,
            })}
          </p>
          <Alert
            type="warning"
            message={t(
              'inspection.urlColumnAutoDownloadWarning',
              'Images mapped to the URL column will be auto-downloaded. If you do not want this, un-map this column.'
            )}
          />
          <br />
          <p>
            {t('inspection.imagesMappedToFollowingInspections', {
              count: affectedInspectionCount,
            })}
          </p>
          {inspectionsNotFound && (
            <>
              <Alert
                type="warning"
                message={t(
                  'inspection.inspectionsNotFoundForImages',
                  'Inspections were not found for some images. You may still attempt to upload.'
                )}
              />
              <br />
            </>
          )}
          {rows.length ? (
            <ValidationTable
              columns={columns}
              dataSource={rows}
              scroll={{ x: TABLE_SCROLL_WIDTH, y: STANDARD_COLUMN_SCROLL_Y.y }}
            />
          ) : null}
        </>
      )}
      <ButtonContainer>
        <Button
          onClick={() => {
            setStep(Steps.DetailsMapping);
          }}
          disabled={uploading}
        >
          {t('common.back', 'Back')}
        </Button>
        <Button
          type="primary"
          onClick={() => handleComplete(allImagesHaveUrls)}
          loading={uploading}
        >
          {t('common.validate', 'Validate')}
        </Button>
      </ButtonContainer>
    </>
  );
};
